/* eslint-disable no-empty-pattern */
import { Flex, Grid } from '@chakra-ui/react';
import { useReviewCreationV1Data } from 'reducers/reviewCreationV1Slice';

import { DottedLineManagerBold } from 'assets/assetsV1/iconComponents/DottedLineManagerBold';
import { ExternalReviewersBold } from 'assets/assetsV1/iconComponents/ExternalReviewersBold';
import { ManagerScoreBold } from 'assets/assetsV1/iconComponents/ManagerScoreBold';
import { PeerBold } from 'assets/assetsV1/iconComponents/PeerBold';
import { SelfScoreBold } from 'assets/assetsV1/iconComponents/SelfScoreBold';
import { SubordinateBold } from 'assets/assetsV1/iconComponents/SubordinateBold';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { GradientBGCard } from 'uiToolkitV1/GradientBGCard/GradientBGCard';
import { TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

const CardsGridWrapper = ({ children }) => (
  <Grid
    gridTemplateColumns="1fr 1fr 1fr"
    gridGap={4}
    // this is for positioning in the parent grid, as this will be a grid inside grid
    gridColumn="2 / span 2"
  >
    {children}
  </Grid>
);

const typeToIconMap = {
  [REVIEW_PROVIDER_TYPES.SELF]: SelfScoreBold,
  [REVIEW_PROVIDER_TYPES.MANAGER]: ManagerScoreBold,
  [REVIEW_PROVIDER_TYPES.PEER]: PeerBold,
  [REVIEW_PROVIDER_TYPES.UPWARD]: SubordinateBold,
  [REVIEW_PROVIDER_TYPES.DLM]: DottedLineManagerBold,
  [REVIEW_PROVIDER_TYPES.EXTERNAL]: ExternalReviewersBold,
};

const VISIBILITY_OPTIONS = [
  {
    providerType: REVIEW_PROVIDER_TYPES.SELF,
    label: ({} = {}) => 'Self',
    description: ({
      hasDLM,
      hasPeer,
      hasUpward,
    }: { hasDLM?: boolean; hasPeer?: boolean; hasUpward?: boolean } = {}) => {
      const hasAnyPeerUpwardDLM = hasDLM || hasPeer || hasUpward;
      const applicableProviders = [];

      if (hasPeer) applicableProviders.push('Peer');
      if (hasUpward) applicableProviders.push('Upward');
      if (hasDLM) applicableProviders.push('DLM');

      return `Cannot see overall score, Can see Manager response${hasAnyPeerUpwardDLM ? `, Cannot see response by ${applicableProviders.join(', ')}` : ''}`;
    },
  },
  {
    providerType: REVIEW_PROVIDER_TYPES.MANAGER,
    label: ({ hasDLM }: { hasDLM?: boolean } = {}) => (hasDLM ? 'Manager & DLM' : 'Manager'),
    description: ({ hasDLM, hasPeer, hasUpward }: { hasDLM?: boolean; hasPeer?: boolean; hasUpward?: boolean } = {}) =>
      `Can see overall score, Can see responses by Self${hasPeer ? ', Peer' : ''}${hasUpward ? ', Upward' : ''}${hasDLM ? ', DLM' : ''}`,
  },
  {
    providerType: REVIEW_PROVIDER_TYPES.PEER,
    label: ({} = {}) => 'Others',
    description: ({ hasPeer, hasUpward }: { hasPeer?: boolean; hasUpward?: boolean } = {}) => {
      let subject = 'Peer';
      if (hasPeer && hasUpward) {
        subject = 'Peer & Upward';
      } else if (hasUpward) {
        subject = 'Upward';
      }
      return `${subject} reviewers can see only their own responses`;
    },
  },
];

const IconLabelComp = ({ providerType, label }) => {
  const Icon = typeToIconMap[providerType];
  return (
    <Flex alignItems="center" gap={2} bg="white" px={2} py={1} borderRadius="16px" mb={2}>
      <Icon color="text.g2" boxSize="24px" />
      <TextLabel bold>{label}</TextLabel>
    </Flex>
  );
};

export const VisibilityCards = () => {
  const { enabledProviderTypes } = useReviewCreationV1Data();

  const hasPeer = enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.PEER);
  const hasUpward = enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.UPWARD);
  const hasDLM = enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.DLM);

  return (
    <CardsGridWrapper>
      <GradientBGCard>
        <IconLabelComp providerType={REVIEW_PROVIDER_TYPES.SELF} label={VISIBILITY_OPTIONS[0].label()} />
        <TextLabelSmall color="text.g2">
          {VISIBILITY_OPTIONS[0].description({ hasDLM, hasPeer, hasUpward })}
        </TextLabelSmall>
      </GradientBGCard>

      <GradientBGCard>
        <IconLabelComp providerType={REVIEW_PROVIDER_TYPES.MANAGER} label={VISIBILITY_OPTIONS[1].label({ hasDLM })} />
        <TextLabelSmall color="text.g2">
          {VISIBILITY_OPTIONS[1].description({ hasDLM, hasPeer, hasUpward })}
        </TextLabelSmall>
      </GradientBGCard>

      {(hasPeer || hasUpward) && (
        <GradientBGCard>
          <IconLabelComp providerType={REVIEW_PROVIDER_TYPES.PEER} label={VISIBILITY_OPTIONS[2].label()} />
          <TextLabelSmall color="text.g2">
            {VISIBILITY_OPTIONS[2].description({ hasDLM, hasPeer, hasUpward })}
          </TextLabelSmall>
        </GradientBGCard>
      )}
    </CardsGridWrapper>
  );
};
